import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Checkbox, LinearProgress } from "@material-ui/core";
import * as _ from 'lodash';

import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import { Link, useHistory } from "react-router-dom";

import AppUtil from "../../../../utilities/AppUtil";
import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import InputSearch from "../../../../components/Input/InputSearch";
import { SYSTEM_ROLE } from "../../../../config/AppConfig";
import AppConfirmDialog from "../../../../components/Dialog/AppConfirmDialog";
import AppTable from "../../../Admin/Component/Table/AppTable";
import TablePaginationActions from "../../../Admin/Component/Table/TablePaginationActions";
import { getAuthInfo } from "../../../../store/slices/auth";
import {
    fetchListUserCoordinator, fetchListUserHospital,
    reduceDeleteUserCoordinator, reduceDeleteUserHospital
} from "../../../../store/slices/clientUser";

const headCells = [
    {
        id: "firstName",
        numeric: "left",
        disablePadding: false,
        label: "label.first_name",
        sort: true,
    },
    {
        id: "lastName",
        numeric: "left",
        disablePadding: false,
        label: "label.last_name",
        sort: true,
    },
    {
        id: "email",
        numeric: "left",
        disablePadding: false,
        label: "label.email",
        sort: true,
    },
    {
        id: "actions",
        numeric: "center",
        disablePadding: false,
        label: "label.actions",
        sort: false,
    },
];

const UserTable = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    const [queries, setQueries] = useState({
        sort: true,
        page_number: 1,
        page_size: 10,
        search_key: "",
        sort_field: "name",
    });
    const user = useSelector(getAuthInfo);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [data, setData] = useState([]);
    const [selected, setSelected] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [searchKey, setSearchKey] = useState("");

    const emptyRows = data.length === 0;

    const _handleDeleteClients = async () => {
        const params = {
            ids: selected
        }
        setDeleting(true);
        setShowDeleteModal(false);
        await dispatch(user.role === SYSTEM_ROLE.HOSPITAL_ADMIN ? reduceDeleteUserHospital(params) : reduceDeleteUserCoordinator(params))
        setSelected([]);
        setDeleting(false);
        if(data.length === 1 || selected.length === data.length) {
            _loadData({ ...queries, page_number: queries.page_number - 1 });
        } else {
            _loadData();
        }
    }

    const _handleSelect = (id) => {
        if (!selected.includes(id)) {
            setSelected([...selected, id]);
        } else {
            setSelected(selected.filter(item => item !== id))
        }
    }

    const _handleSelectAll = () => {
        if (data.filter(item => !item.isOwner && item.id !== user.id).length === selected.length) {
            setSelected([]);
        } else {
            setSelected(_.union(selected, data.filter(item => !item.isOwner && item.id !== user.id).map(item => item.id)));
        }
    }

    const handleChangePage = (event, newPage) => {
        _loadData({ ...queries, page: newPage + 1 });
        AppUtil.scrollToTopById("main-panel");
    };

    const handleChangeRowsPerPage = (event) => {
        _loadData({
            ...queries,
            page: 1,
            size: parseInt(event.target.value),
        });
        AppUtil.scrollToTopById("main-panel");
    };

    const onRequestSort = (event, property) => {
        _loadData({
            ...queries,
            sort_field: property,
            sort:
                property === queries.sort_field ? queries.sort !== true : true,
        });
    };

    const onSearch = (value) => {
        setSearchKey(value);
        _loadData({
            ...queries,
            search_key: value,
            page: 1,
        });
    };

    const _loadData = async ({ page, size, search_key, sort, sort_field } = {}) => {
        const newQueries = {
            ...queries,
        };
        if (page) {
            newQueries.page_number = page;
        }
        if (size) {
            newQueries.page_size = size;
        }
        if (sort_field) {
            newQueries.sort_field = sort_field;
        }
        newQueries.search_key = search_key || "";
        newQueries.sort = !(!sort || false);
        setLoading(true);
        const request = user.role === SYSTEM_ROLE.HOSPITAL_ADMIN ? fetchListUserHospital : fetchListUserCoordinator;
        dispatch(request({
            ...newQueries,
            clientId: user.clientId,
            companyId: user.companyId
        }))
            .then(({ payload }) => {
                setQueries(newQueries);
                setLoading(false);
                if (payload) {
                    setData(payload.content);
                    setTotalElements(payload.totalElements);
                }
            });
    };

    useEffect(() => {
        _loadData();
    }, []);

    // useEffect(() => {
    //     if (userList)
    //         setData(userList);
    // }, [userList])

    return (
        <div>
            <div>
                <AppButton
                    label={t('button.create')}
                    onClick={() => history.push('/users/create')}
                    style={{
                        marginRight: '1rem'
                    }}
                />
                <AppButton
                    label={t('button.delete')}
                    buttonColor={AppColor.pink}
                    onClick={() => setShowDeleteModal(true)}
                    disabled={selected.length === 0 || deleting}
                />
            </div>
            <Box my={3}>
                <InputSearch
                    onSearch={onSearch}
                    searchText={searchKey}
                />
            </Box>
            <AppTable
                onRequestSort={onRequestSort}
                order={queries.sort === true ? "asc" : "desc"}
                orderBy={queries.sort_field}
                rowCount={data.length}
                headCells={headCells}
                numSelected={selected.length}
                onSelectAllClick={_handleSelectAll}
                checkboxIndeterminate={selected.length > 0 && selected.length < data.filter(item => !item.isOwner).length}
                checkboxChecked={data.filter(item => !item.isOwner).length > 0 && selected.length === data.filter(item => !item.isOwner).length}
            >
                <TableBody>
                    {loading && (<TableRow>
                        <TableCell colSpan={7} padding="none">
                            <LinearProgress />
                        </TableCell>
                    </TableRow>)}
                    {data.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell padding="checkbox">
                                {
                                    (row.id !== user.id && !row.isOwner) && (
                                        <Checkbox
                                            checked={selected.indexOf(row.id) !== -1}
                                            inputProps={{ 'aria-labelledby': row.id }}
                                            onClick={() => _handleSelect(row.id)}
                                            color="primary"
                                        />
                                    )
                                }
                            </TableCell>
                            <TableCell>{row.firstName}</TableCell>
                            <TableCell>{row.lastName}</TableCell>
                            <TableCell>
                                {row.email}
                            </TableCell>
                            <TableCell align="center">
                                {row.id !== user.id && !row.isOwner && (
                                    <Link
                                        to={`/users/detail/${row.id}`}
                                        className="text-center"
                                    >
                                        <Button variant="text">
                                            <i className="fas fa-edit" />
                                        </Button>
                                    </Link>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}

                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 }}>
                            <TableCell colSpan={headCells.length + 1} className="text-center">
                                {t("label.no_data")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            colSpan={headCells.length}
                            count={totalElements}
                            rowsPerPage={queries.page_size}
                            page={queries.page_number - 1}
                            SelectProps={{
                                inputProps: { "aria-label": "rows per page" },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelRowsPerPage={`${t("label.rows_per_page")}:`}
                            labelDisplayedRows={({ from, to, count }) =>
                                t("label.from_to", {
                                    from,
                                    to,
                                    count:
                                        count !== -1 ? count : `${t("text.more_than")} ${to}`,
                                })
                            }
                        />
                    </TableRow>
                </TableFooter>
            </AppTable>
            <AppConfirmDialog
                open={showDeleteModal}
                onConfirm={() => _handleDeleteClients()}
                onClose={() => setShowDeleteModal(false)}
                title={t('title.delete_users')}
                content={t('text.delete_users')}
            />
        </div>
    );
};

export default UserTable;
